// /context/BlogContext.js
import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import axios from '../services/axios';
import { useThemeContext } from './ThemeContext';
import { useAuth } from './AuthContext';

const BlogContext = createContext(null);
export const useBlog = () => useContext(BlogContext);

const STORAGE_KEY = 'selected_blog';

function safeParse(json) {
  try {
    return JSON.parse(json);
  } catch {
    return null;
  }
}

export const BlogProvider = ({ children }) => {
  const [blog, setBlog] = useState(null);          // full blog object (latest)
  const [blogChecked, setBlogChecked] = useState(false);
  const [selecting, setSelecting] = useState(false);
  const [error, setError] = useState(null);

  const { authChecked, isAuthenticated } = useAuth();

  // Call hook unconditionally
  const themeCtx = useThemeContext();
  const applyTheme =
    themeCtx?.applyTheme ??
    (() => {
      if (process.env.NODE_ENV !== 'production') {
        console.warn(
          'ThemeProvider is not mounted above BlogProvider (using no-op applyTheme).'
        );
      }
    });

  // Prevent out-of-order async updates
  const opSeq = useRef(0);

  const clearBlog = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setBlog(null);
    setError(null);
    delete axios.defaults.headers.common['X-Blog-ID'];
  }, []);

  const fetchAndApplyFallbackTheme = useCallback(async () => {
    try {
      const r = await axios.get('/api/themes/');
      const list = Array.isArray(r.data) ? r.data : (r.data?.results ?? []);
      const fallback =
        list.find((t) => t.is_default) ||
        list.find((t) => t.name === 'Minimal Coastal') ||
        list[0];
      if (fallback) applyTheme(fallback);
    } catch {
      // no-op: theme fallback failure shouldn't break app
    }
  }, [applyTheme]);

  const refreshBlog = useCallback(
    async (blogObj, seq) => {
      // Prefer id
      const url = blogObj?.id
        ? `/api/blogs/${blogObj.id}/`
        : null;

      if (!url) throw new Error('Cannot refresh blog: missing id/slug');

      const res = await axios.get(url);
      if (seq !== opSeq.current) return null; // stale op

      const fresh = res.data;
      setBlog(fresh);

      if (fresh?.id) {
        axios.defaults.headers.common['X-Blog-ID'] = fresh.id;
      }

      if (fresh?.theme) {
        applyTheme(fresh.theme);
      } else {
        await fetchAndApplyFallbackTheme();
      }

      return fresh;
    },
    [applyTheme, fetchAndApplyFallbackTheme]
  );

  const selectBlog = useCallback(
    async (blogObj) => {
      const seq = ++opSeq.current;
      setSelecting(true);
      setError(null);

      try {
        if (!blogObj || (!blogObj.id && !blogObj.slug)) {
          throw new Error('selectBlog: blog object missing id/slug');
        }

        // If id exists, set header; otherwise wait for refresh to get canonical blog with id
        if (blogObj.id) {
          axios.defaults.headers.common['X-Blog-ID'] = blogObj.id;
        } else {
          delete axios.defaults.headers.common['X-Blog-ID'];
        }

        // Optimistically store + set state so UI updates immediately
        localStorage.setItem(STORAGE_KEY, JSON.stringify(blogObj));
        setBlog(blogObj);

        // If theme present, apply immediately (fast path)
        if (blogObj.theme) {
          applyTheme(blogObj.theme);
          // Still try to refresh to canonical blog (optional). If you don’t want that, comment next line.
          await refreshBlog(blogObj, seq);
          return;
        }

        // Otherwise refresh to pull theme + canonical fields
        await refreshBlog(blogObj, seq);
      } catch (e) {
        if (seq === opSeq.current) {
          console.warn('selectBlog failed; clearing selection:', e);
          setError(e);
          clearBlog();
        }
      } finally {
        if (seq === opSeq.current) setSelecting(false);
      }
    },
    [applyTheme, clearBlog, refreshBlog]
  );

  // On auth ready, restore from storage safely
  useEffect(() => {
    if (!authChecked) return;

    // If not authenticated, just clear and mark checked.
    if (!isAuthenticated) {
      clearBlog();
      setBlogChecked(true);
      return;
    }

    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      setBlogChecked(true);
      return;
    }

    const parsed = safeParse(stored);
    if (!parsed || (!parsed.id && !parsed.slug)) {
      console.warn('Invalid selected_blog in storage; clearing.');
      localStorage.removeItem(STORAGE_KEY);
      setBlogChecked(true);
      return;
    }

    // Attempt restore selection; mark checked when done
    (async () => {
      await selectBlog(parsed);
      setBlogChecked(true);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authChecked, isAuthenticated]);

  const value = useMemo(
    () => ({
      blog,
      blogChecked,
      selecting, // extra (safe to ignore)
      error,     // extra (safe to ignore)
      selectBlog,
      clearBlog,
      refreshBlog: () => (blog ? refreshBlog(blog, ++opSeq.current) : null), // extra
    }),
    [blog, blogChecked, selecting, error, selectBlog, clearBlog, refreshBlog]
  );

  return <BlogContext.Provider value={value}>{children}</BlogContext.Provider>;
};
